import { useRef, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

/**
 * Check if user prefers reduced motion
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Basic scroll-triggered reveal with fade and Y offset
 */
export function useScrollReveal<T extends HTMLElement>(
  selector?: string,
  options?: {
    y?: number
    duration?: number
    stagger?: number
    start?: string
  }
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const elements = selector ? ref.current!.querySelectorAll(selector) : [ref.current!]

      gsap.from(elements, {
        y: options?.y ?? 30,
        opacity: 0,
        duration: options?.duration ?? 0.7,
        stagger: options?.stagger ?? 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: ref.current,
          start: options?.start ?? 'top 85%',
          toggleActions: 'play none none none',
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [selector, options?.y, options?.duration, options?.stagger, options?.start])

  return ref
}

/**
 * Premium reveal with blur and scale effect
 */
export function useLuxeReveal<T extends HTMLElement>(
  selector?: string,
  options?: {
    y?: number
    blur?: number
    scale?: number
    duration?: number
    stagger?: number
  }
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const elements = selector ? ref.current!.querySelectorAll(selector) : [ref.current!]

      gsap.from(elements, {
        y: options?.y ?? 60,
        opacity: 0,
        filter: `blur(${options?.blur ?? 8}px)`,
        scale: options?.scale ?? 0.96,
        duration: options?.duration ?? 1.0,
        stagger: options?.stagger ?? 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [selector, options?.y, options?.blur, options?.scale, options?.duration, options?.stagger])

  return ref
}

/**
 * Grid reveal with stagger pattern
 */
export function useLuxeGrid<T extends HTMLElement>(
  selector: string,
  options?: {
    y?: number
    blur?: number
    scale?: number
    duration?: number
    stagger?: number
  }
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const elements = ref.current!.querySelectorAll(selector)

      gsap.from(elements, {
        y: options?.y ?? 40,
        opacity: 0,
        filter: `blur(${options?.blur ?? 6}px)`,
        scale: options?.scale ?? 0.95,
        duration: options?.duration ?? 0.8,
        stagger: {
          amount: options?.stagger ?? 0.4,
          grid: 'auto',
          from: 'start',
        },
        ease: 'power3.out',
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [selector, options?.y, options?.blur, options?.scale, options?.duration, options?.stagger])

  return ref
}

/**
 * Hero entrance animation (not scroll triggered - runs on mount)
 */
export function useHeroReveal<T extends HTMLElement>(delay = 0.2) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      gsap.from(ref.current!.children, {
        y: 20,
        opacity: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: 'power2.out',
        delay,
      })
    }, ref)

    return () => ctx.revert()
  }, [delay])

  return ref
}

/**
 * Premium hero entrance with blur and scale
 */
export function useLuxeHero<T extends HTMLElement>(delay = 0.2) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      gsap.from(ref.current!.children, {
        y: 40,
        opacity: 0,
        filter: 'blur(8px)',
        scale: 0.97,
        duration: 1.2,
        stagger: 0.18,
        ease: 'power3.out',
        delay,
      })
    }, ref)

    return () => ctx.revert()
  }, [delay])

  return ref
}
